import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, FileText } from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

export const ReverseShoulderReplacement = () => {
  const { theme } = useSite();
  
  const faqs = [
    { question: 'What is a reverse shoulder replacement?', answer: 'Unlike a normal shoulder where the ball is on the arm and socket on the shoulder blade, reverse shoulder replacement switches this - placing the ball on the glenoid and socket on the humerus. This allows the deltoid muscle to lift the arm when the rotator cuff is not functional.' },
    { question: 'Who needs a reverse shoulder replacement?', answer: 'Primarily indicated for: rotator cuff tear arthropathy (arthritis + massive cuff tear), irreparable rotator cuff tears with pseudoparalysis, failed previous shoulder replacement, complex proximal humerus fractures in elderly patients, and tumors.' },
    { question: 'How long does a reverse shoulder last?', answer: 'Registry data shows 90-95% implant survival at 10 years. Being a newer design, long-term data beyond 15 years is still accumulating. Revision rates are slightly higher than anatomic total shoulder replacement.' },
    { question: 'What are the limitations after surgery?', answer: 'While pain relief is excellent, motion may be limited compared to anatomic replacement. External rotation is often reduced. Most patients achieve functional forward elevation. Avoid heavy lifting (>15-20 lbs) long-term. Activities like swimming, golf, and daily tasks are generally possible.' },
    { question: 'What are the specific risks?', answer: 'Scapular notching (bone erosion at scapular neck): 40-60% but usually not clinically significant. Instability/dislocation: 2-5% (higher than anatomic). Acromial stress fractures: 2-4%. Infection: 1-2%. Overall complication rates are slightly higher than anatomic TSA.' }
  ];

  return (
    <div className="min-h-screen" data-testid="reverse-shoulder-page">
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Surgical Procedures
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Reverse Shoulder Replacement in Hyderabad</h1>
            <p className="text-xl text-gray-600">For rotator cuff arthropathy & massive cuff tears. Dr. Deepthi Nandan Reddy at Apollo Hospitals.</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">How It Works</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>The reverse shoulder replacement (RSA) was designed by Dr. Paul Grammont in France and revolutionized treatment of shoulders where the rotator cuff is absent or non-functional.</p>
              <p><strong>Biomechanics:</strong> By reversing the ball-and-socket anatomy and medializing/lowering the center of rotation, the deltoid muscle gains mechanical advantage and can elevate the arm even without a functioning rotator cuff.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Indications</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader><CardTitle>Primary Indications</CardTitle></CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Cuff tear arthropathy</p>
                  <p>• Massive irreparable rotator cuff tears with pseudoparalysis</p>
                  <p>• Failed rotator cuff repair with arthritis</p>
                  <p>• Complex proximal humerus fractures (elderly)</p>
                </CardContent>
              </Card>
              <Card className="border-t-4" style={{ borderTopColor: theme?.primaryHex }}>
                <CardHeader><CardTitle>Expanding Indications</CardTitle></CardHeader>
                <CardContent className="text-gray-600 space-y-2">
                  <p>• Revision of failed shoulder arthroplasty</p>
                  <p>• Tumor reconstruction</p>
                  <p>• Rheumatoid arthritis with cuff insufficiency</p>
                  <p>• Selected fracture sequelae</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Outcomes</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-green-50 p-6 rounded-lg text-center">
                <p className="text-3xl font-bold text-green-700">90-95%</p>
                <p className="text-sm text-gray-600">Pain relief satisfaction</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg text-center">
                <p className="text-3xl font-bold text-blue-700">120-140°</p>
                <p className="text-sm text-gray-600">Average forward elevation</p>
              </div>
              <div className="bg-orange-50 p-6 rounded-lg text-center">
                <p className="text-3xl font-bold text-orange-700">10+ years</p>
                <p className="text-sm text-gray-600">95% implant survival</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg text-sm text-gray-600 space-y-2">
              <p>1. Grammont PM, Baulot E. Delta shoulder prosthesis for rotator cuff rupture. Orthopedics. 1993</p>
              <p>2. Australian Orthopaedic Association National Joint Replacement Registry Annual Reports</p>
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t">Last Updated: January 2025 | Content reviewed by Dr. Deepthi Nandan Reddy, FRCS (Ortho)</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default ReverseShoulderReplacement;
